/**
 * RPG Publisher API client.
 *
 * All requests go through the same-origin proxy (`/api/rpg/*`) which injects
 * the secret `x-api-key` server-side (Vite dev middleware in development, the
 * Netlify `rpg-proxy` function in production). The browser never holds the key.
 *
 * Endpoint shapes were verified against the live RPG Publisher API (OpenAPI
 * `RPG Publisher API v1.0.0`). See _docs/02-rpg-api-reference.md.
 */

import { getAccessToken, refreshAccessToken } from '@/lib/auth/tokens'

const RPG_BASE = '/api/rpg'
/** App-level serverless endpoints (coins, claims, contributions, auth, stripe). */
const APP_BASE = '/api'

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Query params. Uses `unknown` values so callers can pass typed param
 * interfaces (e.g. BookListParams) without assignability friction; values are
 * coerced to strings and empty/nullish entries are dropped.
 */
type Query = Record<string, unknown>

function buildUrl(base: string, path: string, query?: Query): string {
  const url = new URL(base + path, window.location.origin)
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v))
    }
  }
  return url.pathname + url.search
}

async function request<T>(base: string, method: string, path: string, opts: { query?: Query; body?: unknown } = {}): Promise<T> {
  const url = buildUrl(base, path, opts.query)
  // User-scoped app endpoints carry the Atlas access token; the RPG proxy does
  // not (it's keyed by the server-side x-api-key). Guests send no token and the
  // server falls back to the guest cookie.
  const authed = base === APP_BASE

  const exec = (token: string | null) =>
    fetch(url, {
      method,
      headers: {
        ...(opts.body ? { 'content-type': 'application/json' } : {}),
        ...(authed && token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: opts.body ? JSON.stringify(opts.body) : undefined,
      credentials: 'same-origin',
    })

  let res = await exec(authed ? getAccessToken() : null)
  // Access tokens are short-lived; on a 401 with a token present, refresh once
  // (cookie-driven, rotating) and retry transparently.
  if (res.status === 401 && authed && getAccessToken()) {
    const fresh = await refreshAccessToken()
    if (fresh) res = await exec(fresh)
  }

  const text = await res.text()
  const data = text ? safeJson(text) : null

  if (!res.ok) {
    const err = (data ?? {}) as { error?: string; message?: string }
    throw new ApiError(res.status, err.error ?? 'ERROR', err.message ?? res.statusText, data)
  }
  return data as T
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

/** RPG Publisher API (read-only public data through the proxy). */
export const rpg = {
  get: <T>(path: string, query?: Query) => request<T>(RPG_BASE, 'GET', path, { query }),
}

/**
 * Normalize an app path so both `app.get('/coins/balance')` and
 * `app.get('/api/coins/balance')` resolve to the same `/api/coins/balance`
 * URL. (APP_BASE is already `/api`, so a leading `/api` in the path would
 * otherwise double up.)
 */
function appPath(path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`
  return p.startsWith('/api/') || p === '/api' ? p.slice(4) || '/' : p
}

/** App serverless endpoints (user-scoped, auth-checked server-side). */
export const app = {
  get: <T>(path: string, query?: Query) => request<T>(APP_BASE, 'GET', appPath(path), { query }),
  post: <T>(path: string, body?: unknown) => request<T>(APP_BASE, 'POST', appPath(path), { body }),
  patch: <T>(path: string, body?: unknown) => request<T>(APP_BASE, 'PATCH', appPath(path), { body }),
  del: <T>(path: string) => request<T>(APP_BASE, 'DELETE', appPath(path)),
}
